import { SeatHold } from '../models/seat-hold.interface';

/** What the seat holds of a cart say about an order that is waiting to be paid. */
export type SeatHoldDeadline =
  /** the order has no seats: nothing to count down */
  | { kind: 'none' }
  /** some seats of the order are no longer held (the hold lapsed, and the seat may be someone else's) */
  | { kind: 'lost'; lost: number }
  /** every seat is still held; `endsAt` is when the soonest hold lapses, in epoch milliseconds */
  | { kind: 'held'; endsAt: number };

/** The seat each order item was bought for, for the items that have one. */
export function seatUidsOf(items: ReadonlyArray<{ purchaseDetails?: Record<string, unknown> | null }>): string[] {
  const seats = new Set<string>();
  for (const item of items) {
    const seatUid = item.purchaseDetails?.['seatUid'];
    if (typeof seatUid === 'string' && seatUid.trim()) seats.add(seatUid);
  }
  return [...seats];
}

/**
 * When the seats of an order stop being held. A lapsed hold is not listed by the server, so a seat that is
 * missing from `holds` is a seat the cart has lost. Uses the server's own seconds-left, not the browser's
 * clock, which is only used to say what time it is now.
 */
export function seatHoldDeadline(seatUids: readonly string[], holds: readonly SeatHold[], now: number): SeatHoldDeadline {
  const wanted = new Set(seatUids);
  if (wanted.size === 0) return { kind: 'none' };

  const secondsBySeat = new Map<string, number>();
  for (const hold of holds) {
    const previous = secondsBySeat.get(hold.seatUid);
    secondsBySeat.set(hold.seatUid, previous === undefined ? hold.secondsRemaining : Math.min(previous, hold.secondsRemaining));
  }

  const lost = [...wanted].filter((seat) => !secondsBySeat.has(seat)).length;
  if (lost > 0) return { kind: 'lost', lost };

  const soonest = Math.min(...[...wanted].map((seat) => secondsBySeat.get(seat) as number));
  return { kind: 'held', endsAt: now + Math.max(0, soonest) * 1000 };
}

/** Whole seconds left until `endsAt`, never below zero. */
export function secondsUntil(endsAt: number, now: number): number {
  return Math.max(0, Math.ceil((endsAt - now) / 1000));
}

/** "11:05": minutes and seconds, for a countdown. */
export function formatCountdown(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}
